import { useRef, useEffect, useState } from 'react';
import { Box, IconButton, TextareaAutosize, Typography, Tooltip } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import MicIcon from '@mui/icons-material/Mic';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import PromptTemplateSelector from './PromptTemplateSelector';

export default function MessageInput({ onSend, compact = false }) {
  const theme = useTheme();
  const [text, setText] = useState('');
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [draftVoiceText, setDraftVoiceText] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [focused, setFocused] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    el.style.height = 'auto';

    const maxHeight = 200;
    const newHeight = Math.min(el.scrollHeight, maxHeight);

    el.style.height = `${newHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [text, draftVoiceText, listening]);

  function formatUserInput(text) {
    if (!text) return '';

    let formatted = text.trim();

    // Normalize multiple spaces
    formatted = formatted.replace(/\s+/g, ' ');

    // Break into paragraphs for common speech patterns
    formatted = formatted
      .replace(/(\.|!|\?)\s+/g, '$1\n\n') // sentence breaks
      .replace(/(\d+\.|\bfirst\b|\bsecond\b|\bthird\b|\bfourth\b|\bfifth\b)/gi, '\n$1 ')
      .replace(/\n{3,}/g, '\n\n'); // max 2 line breaks

    return formatted.trim();
  }

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    let finalTranscriptRef = '';
    recognition.onresult = (event) => {
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const t = result[0].transcript;

        if (result.isFinal) {
          finalTranscriptRef += t + ' ';
        } else {
          interimTranscript += t;
        }
      }

      // show live text (final + interim), but don't "store" interim forever
      setDraftVoiceText((finalTranscriptRef + interimTranscript).trim());
    };

    recognition.onend = () => {
      finalTranscriptRef = '';
    };

    recognitionRef.current = recognition;
  }, []);

  const confirmVoice = () => {
    setListening(false);

    setText((prev) => {
      if (!draftVoiceText.trim()) return prev;
      return prev ? `${prev} ${draftVoiceText.trim()}` : draftVoiceText.trim();
    });

    setDraftVoiceText('');

    setTimeout(() => {
      recognitionRef.current?.stop();
    }, 0);
  };

  const cancelVoice = () => {
    setListening(false); // UI first
    setDraftVoiceText('');

    setTimeout(() => {
      recognitionRef.current?.stop();
    }, 0);
  };

  const toggleMic = () => {
    if (!recognitionRef.current) return;

    if (listening) {
      recognitionRef.current.stop();
      setListening(false);
    } else {
      recognitionRef.current.start();
      setListening(true);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    setAttachments((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleSend = () => {
    if (!text.trim() && attachments.length === 0) return;
    if (typeof onSend !== 'function') return;
    const formatted = formatUserInput(text);
    onSend({
      text: formatted,
      attachments,
      promptTemplateId: selectedTemplate?.id || null,
      promptTemplateName: selectedTemplate?.name || null
    });
    setText('');
    setAttachments([]);
  };

  const canSend = text.trim().length > 0 || attachments.length > 0;
  const primaryColor = theme.palette.primary.main || '#8B0000';

  return (
    <Box sx={{ px: compact ? 1 : 2, pb: compact ? 1 : 1.5, pt: compact ? 0.5 : 1 }}>
      <Box
        sx={{
          border: focused ? `1.5px solid ${alpha(primaryColor, 0.5)}` : '1px solid #e0e0e0',
          borderRadius: compact ? '12px' : '20px',
          backgroundColor: '#fff',
          maxWidth: compact ? '100%' : 880,
          mx: 'auto',
          transition: 'border-color 0.2s, box-shadow 0.2s',
          boxShadow: focused
            ? `0 0 0 3px ${alpha(primaryColor, 0.08)}`
            : '0 1px 4px rgba(0,0,0,0.04)',
          position: 'relative'
        }}
      >
        {/* ─── Attachments row ─── */}
        {attachments.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, px: 1.5, pt: 1.5, pb: 0.5 }}>
            {attachments.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: '8px',
                  backgroundColor: alpha(primaryColor, 0.06),
                  border: `1px solid ${alpha(primaryColor, 0.15)}`,
                  fontSize: 12,
                  fontWeight: 500,
                  color: theme.palette.text.primary
                }}
              >
                <AttachFileIcon sx={{ fontSize: 14, transform: 'rotate(45deg)', opacity: 0.6 }} />
                {file.name}
                <CloseIcon
                  sx={{ ml: 0.3, fontSize: 13, cursor: 'pointer', opacity: 0.5, '&:hover': { opacity: 1 } }}
                  onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== idx))}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* ─── Template chip row (when selected) ─── */}
        {selectedTemplate && (
          <Box sx={{ px: 1.5, pt: attachments.length > 0 ? 0.5 : 1.5, pb: 0 }}>
            <PromptTemplateSelector selectedTemplate={selectedTemplate} onSelect={setSelectedTemplate} chipOnly />
          </Box>
        )}

        {/* ─── Main input row ─── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', px: compact ? 0.5 : 1, py: compact ? 0.5 : 0.75 }}>
          <input ref={fileInputRef} type="file" hidden multiple onChange={handleFileSelect} />

          {!listening && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pb: 0.5, flexShrink: 0 }}>
              <Tooltip title="Attach files" arrow>
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  size="small"
                  sx={{
                    color: theme.palette.text.disabled,
                    '&:hover': { color: theme.palette.text.secondary, backgroundColor: alpha(primaryColor, 0.06) }
                  }}
                >
                  <AttachFileIcon sx={{ fontSize: 20, transform: 'rotate(45deg)' }} />
                </IconButton>
              </Tooltip>

              {!selectedTemplate && (
                <PromptTemplateSelector selectedTemplate={selectedTemplate} onSelect={setSelectedTemplate} />
              )}
            </Box>
          )}

          <TextareaAutosize
            ref={textareaRef}
            value={listening ? draftVoiceText : text}
            placeholder="Ask Phoenix AI..."
            minRows={1}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (typeof onSend === 'function') {
                  handleSend();
                }
              }
            }}
            style={{
              flex: 1,
              width: '100%',
              resize: 'none',
              fontSize: compact ? '0.95rem' : '0.95rem',
              lineHeight: 1.6,
              padding: compact ? '8px 8px' : '10px 12px',
              border: 'none',
              outline: 'none',
              maxHeight: compact ? '140px' : '200px',
              overflowY: 'auto',
              fontFamily: 'inherit',
              backgroundColor: 'transparent',
              color: theme.palette.text.primary
            }}
          />

          {/* ─── Right-side actions ─── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pb: 0.5, flexShrink: 0 }}>
            {!listening ? (
              <>
                <Tooltip title="Voice input" arrow>
                  <IconButton
                    onClick={toggleMic}
                    size="small"
                    sx={{
                      color: theme.palette.text.disabled,
                      '&:hover': { color: primaryColor, backgroundColor: alpha(primaryColor, 0.06) }
                    }}
                  >
                    <MicIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title={canSend ? 'Send message' : ''} arrow>
                  <span>
                    <IconButton
                      disabled={!canSend}
                      onClick={handleSend}
                      size="small"
                      sx={{
                        ml: 0.25,
                        width: 34,
                        height: 34,
                        backgroundColor: canSend ? primaryColor : 'transparent',
                        color: canSend ? '#fff' : theme.palette.text.disabled,
                        borderRadius: '10px',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: canSend ? alpha(primaryColor, 0.85) : 'transparent'
                        },
                        '&.Mui-disabled': {
                          color: theme.palette.text.disabled,
                          backgroundColor: 'transparent'
                        }
                      }}
                    >
                      <SendIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Cancel" arrow>
                  <IconButton
                    onClick={cancelVoice}
                    size="small"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    <CloseIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Accept voice input" arrow>
                  <IconButton
                    onClick={confirmVoice}
                    size="small"
                    sx={{
                      ml: 0.25,
                      width: 34,
                      height: 34,
                      backgroundColor: primaryColor,
                      color: '#fff',
                      borderRadius: '10px',
                      '&:hover': { backgroundColor: alpha(primaryColor, 0.85) }
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* ─── Hint text ─── */}
      {!compact && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 0.75,
            color: theme.palette.text.disabled,
            fontSize: 11,
            userSelect: 'none'
          }}
        >
          <strong>Enter</strong> to send · <strong>Shift + Enter</strong> for new line
        </Typography>
      )}
    </Box>
  );
}
